import styled from '@emotion/styled'
import { space, device, color, Card, H2, Text } from '@ticketswap/solar'
import { Loudspeaker, Masks, MusicalNote, Sun, Trophy, Voucher } from '@ticketswap/solar/icons'
import ViewportTracking from '~/components/ViewportTracking'
import { EXPLORE_CATEGORY_TAPPED, EXPLORE_CATEGORY_VIEWED } from '~/lib/tracking-events'
import { RouteMap } from '~/lib/routeMap'
import { track } from '~/utils/analytics'
import Link from 'next/link'
import PropTypes from '~/lib/propTypes'

const Categories = styled.div`
  margin-block-start: ${space[16]};
`

const CategoriesGrid = styled.div`
  margin-block-start: ${space[16]};
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: ${space[8]};

  @media ${device.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`

const IconBackground = styled.div`
  background-color: ${({ color }) => color};
  width: ${space[40]};
  height: ${space[40]};
  border-radius: ${space[8]};
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledText = styled(Text)`
  color: ${color.foregroundMuted};
`

const DiscoverCategories = ({ t }) => {
  const handleTracking = async category =>
    await track(EXPLORE_CATEGORY_TAPPED, {
      category,
    })

  const categories = [
    {
      icon: <Sun color={color.lightForeground} size={24} />,
      label: t('common:categories.festivals'),
      color: color.sun,
      route: 'FESTIVALS',
    },
    {
      icon: <MusicalNote color={color.lightForeground} size={24} />,
      label: t('common:categories.concerts'),
      color: color.earth,
      route: 'CONCERTS',
    },
    {
      icon: <Loudspeaker color={color.lightForeground} size={24} />,
      label: t('common:categories.clubs'),
      color: color.darkBackground,
      route: 'CLUBS',
    },
    {
      icon: <Trophy color={color.lightForeground} size={24} />,
      label: t('common:categories.sports'),
      color: color.titan,
      route: 'SPORTS',
    },
    {
      icon: <Masks color={color.lightForeground} size={24} />,
      label: t('common:categories.theatre'),
      color: color.mars,
      route: 'THEATRE_AND_COMEDY',
    },
    {
      icon: <Voucher color={color.lightForeground} size={24} />,
      label: t('common:categories.vouchers'),
      color: color.foregroundMuted,
      route: 'VOUCHERS_AND_DAY_OUT',
    },
  ]

  return (
    <Categories>
      <H2>{t('home:discover.nearby.categories.title')}</H2>
      <StyledText as="p">{t('home:discover.nearby.categories.subtitle')}</StyledText>

      <ViewportTracking
        event={EXPLORE_CATEGORY_VIEWED}
        payload={{
          categories: categories.map(category => category.route),
        }}
      >
        <CategoriesGrid>
          {categories.map(category => (
            <Link
              passHref
              key={category.label}
              prefetch={false}
              href={{
                pathname: RouteMap[category.route],
              }}
            >
              <button as="a" onClick={() => handleTracking(category.route)}>
                <Card
                  title={category.label}
                  leftAdornment={
                    <IconBackground color={category.color}>{category.icon}</IconBackground>
                  }
                />
              </button>
            </Link>
          ))}
        </CategoriesGrid>
      </ViewportTracking>
    </Categories>
  )
}

DiscoverCategories.propTypes = {
  t: PropTypes.func.isRequired,
}

export default DiscoverCategories
